import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowRight, ChevronDown, ChevronRight, Globe, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { useOnboarding } from "@/features/onboarding/OnboardingContext";

export const Route = createFileRoute("/(public)/onboarding/regions")({
  component: AddRegionsPage,
});

function CompanyAccordion({
  companyName,
  regions,
  expanded,
  inputValue,
  onToggle,
  onInputChange,
  onAdd,
  onRemove,
}: {
  companyName: string;
  regions: string[];
  expanded: boolean;
  inputValue: string;
  onToggle: () => void;
  onInputChange: (v: string) => void;
  onAdd: () => void;
  onRemove: (name: string) => void;
}) {
  const { t } = useLingui();
  return (
    <div className="rounded-md overflow-hidden border border-(--gray-200) bg-(--brand-white)">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between h-12 px-4 transition-colors hover:bg-(--gray-50)"
      >
        <span className="text-sm font-medium text-(--brand-black)">
          {companyName}
          {regions.length > 0 && (
            <span className="ml-1.5 text-xs text-(--gray-500)">
              ({plural(regions.length, { one: "# region", other: "# regions" })})
            </span>
          )}
        </span>
        {expanded
          ? <ChevronDown className="w-4 h-4 shrink-0 text-(--gray-500)" />
          : <ChevronRight className="w-4 h-4 shrink-0 text-(--gray-500)" />
        }
      </button>

      {expanded && (
        <div className="flex flex-col gap-2.5 px-4 pb-4 pt-2" style={{ borderTop: "1px solid var(--gray-100)" }}>
          {regions.map((region) => (
            <div
              key={region}
              className="flex items-center justify-between h-10 px-3 rounded-md border border-(--gray-200) bg-(--gray-50)"
            >
              <div className="flex items-center gap-2">
                <Globe className="w-3.5 h-3.5 shrink-0 text-(--gray-500)" />
                <span className="text-sm text-(--brand-black)">{region}</span>
              </div>
              <button
                type="button"
                onClick={() => onRemove(region)}
                className="w-6 h-6 flex items-center justify-center rounded transition-colors hover:bg-red-50 text-(--color-danger)"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          <input
            type="text"
            value={inputValue}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onAdd(); } }}
            placeholder={t`Enter region name...`}
            className="w-full h-10 rounded-md px-3 text-sm outline-none transition-shadow border border-(--gray-200) text-(--brand-black) bg-(--brand-white)"
            onFocus={(e) => { e.currentTarget.style.boxShadow = "0 0 0 3px rgba(109,112,98,0.15)"; e.currentTarget.style.borderColor = "var(--gray-600)"; }}
            onBlur={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.borderColor = "var(--gray-200)"; }}
          />

          <button
            type="button"
            onClick={onAdd}
            className="flex items-center gap-1.5 text-sm font-medium w-fit transition-opacity hover:opacity-70 text-(--gray-600)"
          >
            <Plus className="w-4 h-4" />
            {t`Add another region`}
          </button>
        </div>
      )}
    </div>
  );
}

function AddRegionsPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { companyNames, regionsByCompany: savedRegions, setRegionsByCompany } = useOnboarding();

  const [regionsByCompany, setLocal] = useState<Record<string, string[]>>(
    () => Object.fromEntries(companyNames.map((c) => [c, savedRegions[c] ?? []])),
  );
  const [expandedByCompany, setExpanded] = useState<Record<string, boolean>>({});
  const [inputByCompany, setInput] = useState<Record<string, string>>({});

  const addRegion = (companyName: string) => {
    const name = (inputByCompany[companyName] ?? "").trim();
    if (!name) return;
    const existing = regionsByCompany[companyName] ?? [];
    if (existing.some((r) => r.toLowerCase() === name.toLowerCase())) {
      toast.error(t`Region already added for this concept`);
      return;
    }
    setLocal((prev) => ({ ...prev, [companyName]: [...existing, name] }));
    setInput((prev) => ({ ...prev, [companyName]: "" }));
  };

  const removeRegion = (companyName: string, region: string) => {
    setLocal((prev) => ({
      ...prev,
      [companyName]: (prev[companyName] ?? []).filter((r) => r !== region),
    }));
  };

  const handleContinue = () => {
    setRegionsByCompany(regionsByCompany);
    navigate({ to: "/onboarding/locations" });
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <h1 className="font-semibold leading-tight text-(--brand-black)" style={{ fontSize: "2rem" }}>
          {t`Add regions`}
        </h1>
        <p className="text-sm leading-relaxed text-(--gray-500)">
          {t`Regions help organize locations geographically. Add regions under each concept.`}
        </p>
      </div>

      <div className="flex flex-col gap-2">
        {companyNames.length === 0 ? (
          <p className="text-sm text-(--gray-400)">
            {t`No concepts added yet. Go back and add at least one concept first.`}
          </p>
        ) : (
          companyNames.map((companyName) => (
            <CompanyAccordion
              key={companyName}
              companyName={companyName}
              regions={regionsByCompany[companyName] ?? []}
              expanded={expandedByCompany[companyName] ?? false}
              inputValue={inputByCompany[companyName] ?? ""}
              onToggle={() => setExpanded((prev) => ({ ...prev, [companyName]: !prev[companyName] }))}
              onInputChange={(v) => setInput((prev) => ({ ...prev, [companyName]: v }))}
              onAdd={() => addRegion(companyName)}
              onRemove={(r) => removeRegion(companyName, r)}
            />
          ))
        )}
      </div>

      <div className="flex flex-col gap-3">
        <button
          type="button"
          onClick={handleContinue}
          className="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-md transition-opacity hover:opacity-90 bg-(--brand-black)"
        >
          {t`Continue`} <ArrowRight className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => { setRegionsByCompany({}); navigate({ to: "/onboarding/locations" }); }}
          className="text-sm font-medium text-center transition-opacity hover:opacity-70 text-(--gray-600)"
        >
          {t`Skip for now`}
        </button>
      </div>
    </div>
  );
}
